// src/components/HeaderAcces/HeaderAcces.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import conectaLogo from "../../assets/generales/logo.svg";
import TituloHome from "../../assets/generales/conecta-peru-footer.svg";
import "./HeaderAcces.css";
import CustomModal from "../../components/CustomModal/CustomModal";

const HeaderAcces = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Hook para obtener la ruta actual
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const menuRef = useRef(null);

  // Estado para guardar los datos del usuario decodificados del token
  const [userData, setUserData] = useState(null);

  // Estados para el modal de error/sesión expirada
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState({
    title: "",
    message: "",
    type: "",
  });

  // Nuevo estado para el modal de confirmación de cierre de sesión
  const [showConfirmLogoutModal, setShowConfirmLogoutModal] = useState(false);

  // Función para cerrar el modal de error y luego cerrar la sesión
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    handleLogout();
  };

  // Función que realiza el cierre de sesión
  const handleLogout = () => {
    localStorage.removeItem("userToken");
    navigate("/login");
  };

  // Función para manejar el clic en "Cerrar sesión" (abre el modal de confirmación)
  const handleLogoutClick = () => {
    setMenuOpen(false); // Cierra el menú desplegable
    setShowConfirmLogoutModal(true); // Abre el modal de confirmación
  };

  // Función para confirmar el cierre de sesión (clic en "Aceptar" en el modal)
  const handleConfirmLogout = () => {
    setShowConfirmLogoutModal(false);
    handleLogout();
  };

  // Función para cancelar el cierre de sesión (clic en "Cancelar" en el modal)
  const handleCancelLogout = () => {
    setShowConfirmLogoutModal(false);
  };

  // Efecto para obtener los datos del usuario y validar el token
  useEffect(() => {
    const token = localStorage.getItem("userToken");

    if (!token) {
      setErrorModalContent({
        title: "Sesión Terminada",
        message:
          "Por motivos de seguridad, su sesión ha finalizado. Por favor, vuelva a iniciar sesión.",
        type: "info",
      });
      setShowErrorModal(true);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        setErrorModalContent({
          title: "Sesión Expirada",
          message:
            "Tu sesión ha expirado. Por favor, inicia sesión para continuar.",
          type: "error",
        });
        setShowErrorModal(true);
        return;
      }

      setUserData(decodedToken);
    } catch (error) {
      console.error("Error al decodificar el token:", error);
      setErrorModalContent({
        title: "Error de Sesión",
        message:
          "Ha ocurrido un error con tu sesión. Por favor, vuelve a iniciar sesión.",
        type: "error",
      });
      setShowErrorModal(true);
    }
  }, [navigate]);

  // Efecto para manejar el cambio de tamaño de la ventana
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cierra menú cuando haces clic fuera en móvil (porque en desktop es hover)
  useEffect(() => {
    if (!isMobile) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isMobile]);

  // Toggle menú en móvil solo con clic
  const toggleMenu = () => {
    if (isMobile) setMenuOpen(!menuOpen);
  };

  // Si no hay datos de usuario y no se muestra el modal de error, no renderizamos el componente
  if (!userData && !showErrorModal) {
    return null;
  }

  // Obtenemos el nombre y la inicial del usuario de forma dinámica
  const nombreUsuario = userData ? userData.nombreuser : "";
  const inicialUsuario = nombreUsuario ? nombreUsuario[0].toUpperCase() : "";

  // Lógica para ocultar el botón si la ruta actual es /create-group
  const isCreateGroupPage = location.pathname === "/create-group";

  return (
    <>
      <header className="home-header sticky-top bg-black">
        <div className="container d-flex justify-content-between align-items-center py-2">
          <Link to="/welcome" className="d-none d-md-block">
            <img src={conectaLogo} alt="Conéctate Perú" className="logo-home" />
          </Link>

          <div className="d-flex align-items-center">
            <img
              src={TituloHome}
              alt="Conéctate Perú"
              className="logo-welcome me-3"
            />
          </div>

          <div className="d-flex align-items-center gap-3">
            {!isMobile && !isCreateGroupPage && (
              <Link to="/create-group" className="btn btn-danger btn-sm me-2">
                Crea tu grupo
              </Link>
            )}

            <div className="position-relative user-menu" ref={menuRef}>
              <button
                aria-label="Menú de usuario"
                className="btn btn-navicon p-0 d-flex align-items-center"
                onClick={toggleMenu}
              >
                <span className="avatar-user rounded-circle fw-bold fs-4 me-2">
                  {inicialUsuario}
                </span>
                <i className="bi bi-list text-white fs-3"></i>
              </button>

              <div
                className={`user-dropdown-menu shadow ${
                  menuOpen ? "show" : ""
                }`}
              >
                {isMobile && (
                  <Link
                    to="/create-group"
                    className="dropdown-item custom py-2"
                    onClick={() => setMenuOpen(false)}
                  >
                    🏁 Crea tu grupo
                  </Link>
                )}
                <div className="dropdown-item disabled custom fw-bold">
                  Hola, {nombreUsuario}!
                </div>
                <div className="dropdown-divider my-1"></div>
                <Link
                  to="/mis-eventos"
                  className="dropdown-item custom"
                  onClick={() => setMenuOpen(false)}
                >
                  Mis eventos
                </Link>
                <Link
                  to="/mis-grupos"
                  className="dropdown-item custom"
                  onClick={() => setMenuOpen(false)}
                >
                  Mis grupos
                </Link>
                <Link
                  to="/perfil"
                  className="dropdown-item custom"
                  onClick={() => setMenuOpen(false)}
                >
                  Mi Perfil
                </Link>
                <div className="dropdown-divider my-1"></div>
                <button
                  className="dropdown-item custom text-danger"
                  onClick={handleLogoutClick}
                >
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Modal para manejar mensajes de sesión expirada/error */}
      <CustomModal
        isOpen={showErrorModal}
        title={errorModalContent.title}
        message={errorModalContent.message}
        onClose={handleCloseErrorModal}
        type={errorModalContent.type}
      />

      {/* Modal de confirmación de cierre de sesión */}
      <CustomModal
        isOpen={showConfirmLogoutModal}
        title="Confirmar Cierre de Sesión"
        message="¿Estás seguro de que quieres cerrar tu sesión?"
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout} // Asumimos que CustomModal tiene un prop onConfirm
        type="confirm" // Tipo de modal para manejar lógicas de botones
      />
    </>
  );
};

export default HeaderAcces;
